import Portal from '@reach/portal';
import { TooltipPopup as TooltipPopup$1, useTooltip } from '@reach/tooltip';
import React__default, { useMemo, Children, cloneElement } from 'react';
import styled, { css } from 'styled-components';
import { HappyWebSystemError } from '../base/SystemComponent.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { CheckboxInput } from '../inputs/CheckboxInput.js';
import { Box } from '../layout/Box.js';
import { setPortalLayer } from '../styles/stylePropTypes.js';

const StyledTooltipPopup = styled(TooltipPopup$1)`
  &[data-reach-tooltip] {
    background: ${COLOR_PALETTE.slateGrey100};
    color: ${COLOR_PALETTE.white};
    border: none;
    border-radius: 0.25rem;
    padding: 0.5em 1em;
    font-size: 0.85rem;
    ${setPortalLayer()}
  }
`;
const StyledTooltipTrianglePopup = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid ${COLOR_PALETTE.slateGrey100};
  ${setPortalLayer()}

  ${(props)=>props.triggerRect ? getTriangleCenteredPosition(props.triggerRect, props.popupPosition) : ""}
`;
const getTriangleCenteredPosition = (triggerRect, popupPosition)=>{
    switch(popupPosition){
        case "top":
            return getTriangleCenteredPositionTop({
                triggerRect
            });
        case "left":
            return getTriangleCenteredPositionLeft({
                triggerRect
            });
        case "right":
            return getTriangleCenteredPositionRight({
                triggerRect
            });
        default:
            return getTriangleCenteredPositionBottom({
                triggerRect
            });
    }
};
const getTriangleCenteredPositionTop = ({ triggerRect  })=>{
    return css`
    left: ${triggerRect && triggerRect.left - 10 + triggerRect.width / 2}px;
    top: ${triggerRect && triggerRect.top - 10 + window.pageYOffset}px;
    transform: rotate(180deg);
  `;
};
const getTriangleCenteredPositionBottom = ({ triggerRect  })=>{
    return css`
    left: ${triggerRect && triggerRect.left - 10 + triggerRect.width / 2}px;
    top: ${triggerRect && triggerRect.bottom + window.pageYOffset}px;
  `;
};
const getTriangleCenteredPositionLeft = ({ triggerRect  })=>{
    return css`
    left: ${triggerRect && triggerRect.left - 18 + window.pageXOffset}px;
    top: ${triggerRect && triggerRect.top + triggerRect.height / 2 - 5 + window.pageYOffset}px;
    transform: rotate(90deg);
  `;
};
const getTriangleCenteredPositionRight = ({ triggerRect  })=>{
    return css`
    left: ${triggerRect && triggerRect.right + window.pageXOffset}px;
    top: ${triggerRect && triggerRect.top + triggerRect.height / 2 - 5 + window.pageYOffset}px;
    transform: rotate(270deg);
  `;
};
const getCenteredPositionFunc = (popupPosition)=>{
    switch(popupPosition){
        case "top":
            return getCenteredPositionTop;
        case "left":
            return getCenteredPositionLeft;
        case "right":
            return getCenteredPositionRight;
        default:
            return getCenteredPositionBottom;
    }
};
const getCenteredPositionTop = (triggerRect, tooltipRect)=>{
    if (!triggerRect || !tooltipRect) {
        return {};
    }
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const left = triggerCenter - tooltipRect.width / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - 2;
    return {
        left: Math.min(Math.max(2, left), maxLeft) + window.pageXOffset,
        top: triggerRect.top - 36 + window.pageYOffset
    };
};
const getCenteredPositionBottom = (triggerRect, tooltipRect)=>{
    if (!triggerRect || !tooltipRect) {
        return {};
    }
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const left = triggerCenter - tooltipRect.width / 2;
    const maxLeft = window.innerWidth - tooltipRect.width - 2;
    return {
        left: Math.min(Math.max(2, left), maxLeft) + window.pageXOffset,
        top: triggerRect.bottom + 8 + window.pageYOffset
    };
};
const getCenteredPositionLeft = (triggerRect, tooltipRect)=>{
    if (!triggerRect || !tooltipRect) {
        return {};
    }
    const triggerYCenter = triggerRect.top + triggerRect.height / 2;
    const left = triggerRect.left - tooltipRect.width - 8;
    const maxLeft = window.innerWidth - 2;
    return {
        left: Math.min(Math.max(2, left), maxLeft) + window.pageXOffset,
        top: triggerYCenter - tooltipRect.height / 2 + window.pageYOffset
    };
};
const getCenteredPositionRight = (triggerRect, tooltipRect)=>{
    if (!triggerRect || !tooltipRect) {
        return {};
    }
    const triggerYCenter = triggerRect.top + triggerRect.height / 2;
    return {
        left: triggerRect.right + 10 + window.pageXOffset,
        top: triggerYCenter - tooltipRect.height / 2 + window.pageYOffset
    };
};
const TooltipPopup = ({ id , triggerRect , isVisible , label , popupPosition  })=>{
    if (!label || !isVisible) {
        return null;
    }
    const calcPosition = getCenteredPositionFunc(popupPosition);
    return React__default.createElement(React__default.Fragment, null, React__default.createElement(Portal, null, React__default.createElement(StyledTooltipTrianglePopup, {
        "data-hpy-web-tooltip-popup-triangle": true,
        triggerRect: triggerRect,
        popupPosition: popupPosition
    })), React__default.createElement(StyledTooltipPopup, {
        "data-hpy-web-tooltip-popup": true,
        id: id,
        label: label,
        triggerRect: triggerRect,
        isVisible: isVisible,
        position: calcPosition
    }));
};
TooltipPopup.dataId = "data-hpy-web-tooltip-popup";
TooltipPopup.displayName = "TooltipPopup";
const isElement = (child)=>{
    return child?.type !== React__default.Fragment && child?.props !== undefined;
};
const isSystemComponent = (child)=>{
    return child?.type?.dataId !== undefined;
};
/**
 * Functional Tooltip wrapper.
 *
 * Allows compositional use of Tooltip, wrapping desired tooltip hover element.
 *
 * @example
 * <Tooltip label="Save">
 *   <button>💾</button>
 * </Tooltip>
 */ const Tooltip = ({ position ="bottom" , children , label , forwardedRef =null  })=>{
    const [trigger, tooltip] = useTooltip({
        ref: forwardedRef
    });
    const element = useMemo(()=>{
        const childComps = Children.toArray(children);
        if (childComps === undefined || childComps.length > 1) {
            throw new HappyWebSystemError("Tooltip", "To correctly overload event handlers, a single child element must be provided.");
        }
        const childComp = childComps[0];
        if (!isElement(childComp)) {
            throw new HappyWebSystemError("Tooltip", "To correctly overload event handlers, a valid child element must be provided. Portals, fragments, strings .etc are not valid.");
        }
        let el = children;
        // Some of the Happy Web System components need to passthrough props/ref to their underlying inputs. This makes the
        // handlers for Tooltip not arrive on the correct element, so we need to wrap them in these special cases
        if (isSystemComponent(childComp) && childComp?.type?.dataId === CheckboxInput.dataId) {
            el = React__default.createElement(Box, null, childComp);
        } else if (childComp?.props?.disabled) {
            el = React__default.createElement(Box, null, childComp);
        }
        return el;
    }, [
        children
    ]);
    return React__default.createElement(React__default.Fragment, null, cloneElement(element, trigger), React__default.createElement(TooltipPopup, {
        "data-hpy-web-tooltip": true,
        ...tooltip,
        label: label,
        popupPosition: position
    }));
};
Tooltip.dataId = "data-hpy-web-tooltip";
Tooltip.displayName = "Tooltip";

export { Tooltip };
